// Stale-chunk recovery. After a redeploy, an already-open page holds a stale
// HTML document that references old hashed chunk URLs. Those 404, so any lazy
// `import()` (e.g. the dynamically loaded AppProviders) rejects and — with no
// covering boundary — the page blanks. The standard Next.js remedy is a single
// hard reload so the browser fetches the fresh document + current chunks.

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Loading chunk',
  'Loading CSS chunk',
  'Importing a module script failed',
];

// sessionStorage flag so a recurring failure after the reload does not loop.
// Cleared automatically when the tab closes; we also clear it on a clean load.
export const CHUNK_RELOAD_FLAG = 'r2h_chunk_reloaded';

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;
  const message = error.message || '';
  return CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
}

/**
 * If `error` looks like a stale-chunk failure, trigger a one-time hard reload.
 * Returns true when a reload was triggered, false otherwise (not a chunk error,
 * already reloaded once, or no browser environment).
 */
export function recoverFromChunkError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  if (typeof window === 'undefined') return false;

  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      // We already reloaded once for a chunk error; reloading again would loop.
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage can throw (private mode / disabled). Fall through and still
    // attempt the reload — a one-off reload is better than a blank screen.
  }

  window.location.reload();
  return true;
}

/**
 * Clear the reload guard. Call once on a clean app mount so a *future*,
 * unrelated stale-chunk window can recover with its own single reload.
 */
export function clearChunkReloadFlag(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
  } catch {
    // ignore
  }
}
